import { ApiClientError } from '../api/client';
import type { PosStore } from './store';

/**
 * Kaufbeleg per E-Mail an der Kasse (Welle 5d). Der Kaufbeleg braucht den Server; offline wartet die Anfrage (Verkauf-Kennung und
 * Adresse) im lokalen Speicher und geht erst NACH der Bestätigung des Verkaufs hinaus (sonst antwortet der Server 404). Die
 * Adresse verschwindet vom Gerät, sobald der Versand gelungen, abgelehnt oder aufgegeben ist.
 */

/** Antworten „nicht gefunden“ trotz bestätigtem Verkauf, bevor aufgegeben wird. */
export const INVOICE_MAX_NOT_FOUND = 5;
/** So lange wartet eine Anfrage höchstens auf dem Gerät (Datenschutz): danach wird sie samt Adresse gelöscht. */
/** Pause zwischen zwei Versuchen nach „nicht gefunden“ (die Läufe folgen sonst im Sekundentakt dem Abgleich). */
export const INVOICE_RETRY_PAUSE_MS = 30_000;
export const INVOICE_MAX_AGE_MS = 24 * 60 * 60_000;

/** Ausgang einer Anfrage, der der Kassenkraft angezeigt wird. */
export type InvoiceOutcome = { kind: 'sent' } | { kind: 'failed'; message: string };

export type InvoiceSender = (
  saleClientUuid: string,
  email: string,
  requestId: string,
) => Promise<void>;

const MESSAGES = {
  limit:
    'Zu viele Anfragen in kurzer Zeit. Bitte in einigen Minuten noch einmal versuchen. Die Adresse wurde vom Gerät gelöscht.',
  notFound:
    'Der Server kennt den Verkauf nicht oder der Kaufbeleg ist für diesen Basar nicht eingeschaltet. Der Kaufbeleg wurde nicht versendet, die Adresse wurde vom Gerät gelöscht.',
  expired:
    'Der Kaufbeleg konnte nicht gesendet werden (zu lange ohne Verbindung). Die Adresse wurde vom Gerät gelöscht.',
  saleFailed:
    'Der Verkauf wurde vom Server nicht übernommen, deshalb gibt es keinen Kaufbeleg. Die Adresse wurde vom Gerät gelöscht.',
  invalid:
    'Die Adresse ist ungültig. Der Kaufbeleg wurde nicht versendet, die Adresse wurde vom Gerät gelöscht.',
  purged:
    'Der Kaufbeleg wurde nicht gesendet (an dieser Kasse ausgeschaltet oder Gerät gesperrt). Die Adresse wurde vom Gerät gelöscht.',
  other:
    'Der Kaufbeleg konnte nicht versendet werden. Die Adresse wurde vom Gerät gelöscht; bitte noch einmal versuchen.',
  auth: 'Die Anmeldung der Kasse wird vom Server abgelehnt.',
  server: 'Der Server antwortet derzeit nicht richtig.',
} as const;

/** Meldung zu einer Ablehnung des Servers (409: Grenzen je Adresse, Basar und Tag, stornierter Verkauf, Basar nicht im Verkauf). */
export function invoiceFailure(error: ApiClientError): string {
  if (error.status === 409) {
    const text = error.message.trim();
    return `${text === '' ? 'Der Kaufbeleg kann nicht versendet werden.' : text} Die Adresse wurde vom Gerät gelöscht.`;
  }
  if (error.status === 400 || error.status === 422) return MESSAGES.invalid;
  if (error.status === 429 || error.code === 'RATE_LIMITED') return MESSAGES.limit;
  return MESSAGES.other;
}

export interface InvoiceRun {
  finished: { saleClientUuid: string; outcome: InvoiceOutcome }[];
  /** Anfragen, die weiter warten (offline, Verkauf noch nicht bestätigt, Serverfehler). */
  waiting: number;
  /** Grund, falls der Server dauerhaft nicht annimmt (Anmeldung abgelehnt, Serverfehler); Netzfehler zählen nicht. */
  trouble?: string | undefined;
}

/**
 * Ein Durchlauf über die wartenden Anfragen. `canSend: false` (offline, Stand noch unbekannt) löscht nur Abgelaufenes;
 * `purge` (Funktion ausgeschaltet, Gerätekasse oder Kasse gesperrt) löscht alle wartenden Anfragen samt Adresse.
 * Offline oder bei einem Serverfehler (Netz, 5xx, 401) bricht der Lauf ab und lässt alles stehen. Ein Verkauf, den der Server
 * noch nicht bestätigt hat, wartet (kein Zählen als „nicht gefunden“).
 */
export async function runInvoiceQueue(
  store: PosStore,
  send: InvoiceSender,
  options: { canSend: boolean; purge?: boolean; nowMs: number },
): Promise<InvoiceRun> {
  const run: InvoiceRun = { finished: [], waiting: 0 };
  const finish = async (saleClientUuid: string, outcome: InvoiceOutcome): Promise<void> => {
    await store.settleInvoice(saleClientUuid, null);
    run.finished.push({ saleClientUuid, outcome });
  };
  const list = await store.invoiceRequests();
  let stopped = false;
  for (const request of list) {
    if (options.purge === true) {
      await finish(request.saleClientUuid, { kind: 'failed', message: MESSAGES.purged });
      continue;
    }
    if (options.nowMs - request.createdAtMs > INVOICE_MAX_AGE_MS) {
      await finish(request.saleClientUuid, { kind: 'failed', message: MESSAGES.expired });
      continue;
    }
    if (!options.canSend || stopped) {
      run.waiting += 1;
      continue;
    }
    const state = await store.saleState(request.saleClientUuid);
    if (state === 'failed') {
      await finish(request.saleClientUuid, { kind: 'failed', message: MESSAGES.saleFailed });
      continue;
    }
    if (state !== 'synced') {
      run.waiting += 1;
      continue;
    }
    if (
      request.lastTryMs !== undefined &&
      options.nowMs - request.lastTryMs < INVOICE_RETRY_PAUSE_MS
    ) {
      run.waiting += 1;
      continue;
    }
    try {
      await send(request.saleClientUuid, request.email, request.requestId);
      await finish(request.saleClientUuid, { kind: 'sent' });
    } catch (caught) {
      if (!(caught instanceof ApiClientError) || caught.status === 0) {
        stopped = true;
        run.waiting += 1;
      } else if (caught.status >= 500) {
        stopped = true;
        run.waiting += 1;
        run.trouble = MESSAGES.server;
      } else if (caught.status === 401 || caught.status === 403) {
        // Sitzung abgelaufen oder andere Person: nichts verwerfen, später erneut
        stopped = true;
        run.waiting += 1;
        run.trouble = MESSAGES.auth;
      } else if (caught.status === 404) {
        const notFound = request.notFound + 1;
        if (notFound >= INVOICE_MAX_NOT_FOUND) {
          await finish(request.saleClientUuid, { kind: 'failed', message: MESSAGES.notFound });
        } else {
          await store.settleInvoice(request.saleClientUuid, {
            ...request,
            notFound,
            lastTryMs: options.nowMs,
          });
          run.waiting += 1;
        }
      } else {
        await finish(request.saleClientUuid, { kind: 'failed', message: invoiceFailure(caught) });
      }
    }
  }
  return run;
}
